import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Switch } from '@/app/components/ui/switch';
import { TabsContent } from '@/app/components/ui/tabs';
import { CharBar } from '../CharBar';
import { updateVariant } from '../useTemplateEditor';
import type { CalloutItem, SitelinkItem } from '@/lib/api';
import type { EditorReady } from './types';

export function ExtensionsTab({ ed }: { ed: EditorReady }) {
  const { templateSet, updateTemplateContent, getCharInfo } = ed;
  return (
    <TabsContent value="extensions" className="space-y-4">
      {/* Callouts */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Callout Extensions</CardTitle>
              <CardDescription>Short promotional text (25 characters max)</CardDescription>
            </div>
            <Button onClick={() => {
              const newCallout: CalloutItem = { id: crypto.randomUUID(), variants: [{ variant_order: 1, template_string: '', enabled: true }] };
              updateTemplateContent('callouts', [...templateSet.template_content.callouts, newCallout]);
            }}>
              <Plus className="size-4 mr-2" />Add Callout
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {templateSet.template_content.callouts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">No callouts yet.</div>
          ) : (
            templateSet.template_content.callouts.map((callout, index) => (
              <Card key={callout.id} className="border-2">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Callout {index + 1}</CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => updateTemplateContent('callouts', templateSet.template_content.callouts.filter(c => c.id !== callout.id))}>
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {callout.variants.map((variant) => (
                    <div key={variant.variant_order} className="space-y-2 p-3 border rounded-lg">
                      <Input
                        value={variant.template_string}
                        onChange={e => updateTemplateContent('callouts', templateSet.template_content.callouts.map(c => {
                          if (c.id !== callout.id) return c;
                          return { ...c, variants: updateVariant(c.variants, variant.variant_order, { template_string: e.target.value }) };
                        }))}
                        placeholder="e.g., Livraison gratuite"
                      />
                      <CharBar {...getCharInfo(variant.template_string, 25)} limit={25} />
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))
          )}
        </CardContent>
      </Card>

      {/* Sitelinks */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Sitelink Extensions</CardTitle>
              <CardDescription>Additional links to specific pages</CardDescription>
            </div>
            <Button onClick={() => {
              const newSitelink: SitelinkItem = {
                id: crypto.randomUUID(), sitelink_text: '', sitelink_url: '',
                description_1: '', description_2: '', enabled: true,
                display_order: templateSet.template_content.sitelinks.length + 1,
              };
              updateTemplateContent('sitelinks', [...templateSet.template_content.sitelinks, newSitelink]);
            }}>
              <Plus className="size-4 mr-2" />Add Sitelink
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {templateSet.template_content.sitelinks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">No sitelinks yet.</div>
          ) : (
            templateSet.template_content.sitelinks.map((sitelink) => (
              <Card key={sitelink.id} className="border-2">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-base">Sitelink {sitelink.display_order}</CardTitle>
                      <Switch
                        checked={sitelink.enabled}
                        onCheckedChange={checked => updateTemplateContent('sitelinks', templateSet.template_content.sitelinks.map(s => s.id === sitelink.id ? { ...s, enabled: checked } : s))}
                      />
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => updateTemplateContent('sitelinks', templateSet.template_content.sitelinks.filter(s => s.id !== sitelink.id).map((s, i) => ({ ...s, display_order: i + 1 })))}>
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Link Text (25 char)</Label>
                      <Input value={sitelink.sitelink_text} onChange={e => updateTemplateContent('sitelinks', templateSet.template_content.sitelinks.map(s => s.id === sitelink.id ? { ...s, sitelink_text: e.target.value } : s))} placeholder="e.g., Nos Services" disabled={!sitelink.enabled} />
                      <CharBar {...getCharInfo(sitelink.sitelink_text, 25)} limit={25} />
                    </div>
                    <div className="space-y-2">
                      <Label>URL</Label>
                      <Input value={sitelink.sitelink_url} onChange={e => updateTemplateContent('sitelinks', templateSet.template_content.sitelinks.map(s => s.id === sitelink.id ? { ...s, sitelink_url: e.target.value } : s))} placeholder="https://example.com/services" disabled={!sitelink.enabled} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Description 1 (35 char)</Label>
                      <Input value={sitelink.description_1 || ''} onChange={e => updateTemplateContent('sitelinks', templateSet.template_content.sitelinks.map(s => s.id === sitelink.id ? { ...s, description_1: e.target.value } : s))} placeholder="e.g., Découvrez nos offres" disabled={!sitelink.enabled} />
                      <CharBar {...getCharInfo(sitelink.description_1 || '', 35)} limit={35} />
                    </div>
                    <div className="space-y-2">
                      <Label>Description 2 (35 char)</Label>
                      <Input value={sitelink.description_2 || ''} onChange={e => updateTemplateContent('sitelinks', templateSet.template_content.sitelinks.map(s => s.id === sitelink.id ? { ...s, description_2: e.target.value } : s))} placeholder="e.g., Promotions exclusives" disabled={!sitelink.enabled} />
                      <CharBar {...getCharInfo(sitelink.description_2 || '', 35)} limit={35} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </CardContent>
      </Card>
    </TabsContent>
  );
}
